// Phase 3 — /play/foundation?skill=<ms.read.*> — foundation reading
// session (letter names + formation), skill-parameterized via search.
//
// PR-D / PR-3 — PLACEMENT GUARD (unbypassable chokepoint). A placement-
// complete kid placed ABOVE the foundation tier (e.g. reading.09) must never
// be served a below-floor foundation skill (the "6yo gets trace-b" bug, #3).
// The free-choice CTA, the journey stone-tap, a deep link, and the browser
// back button all land HERE, so the guard lives in beforeLoad (runs before the
// component mounts on every navigation to the route) rather than at the
// call-sites, which a deep-link / back-nav bypasses (R-5).
//
// `practice` exempts an EXPLICIT parent choice (SkillTree.practiceTargetForSkill
// → "practice letter formation"): a parent deliberately drilling a below-floor
// foundation skill is allowed.
import { createFileRoute, redirect } from "@tanstack/react-router";
import { FoundationSession } from "@/screens/FoundationSession";
import { listChildren } from "@/childrenStore.js";
import { loadProgressForActiveChild } from "@/App/progress";
import { foundationGuardRedirect } from "@/lib/engine/evidenceFloor";
import { floorValveLoweredForChild } from "@/lib/engine/floorValve";
import { KidRouteError } from "@/components/RouterErrorFallback";
import { parseResumeParam, parseSeedParam } from "@/lib/engine/sessionDeepLink";

interface FoundationSearch {
  skill: string;
  practice?: boolean;
  // MA-208 entry-point slice: mode="trace" is a dedicated, non-FOUNDATION_
  // SKILL_IDS entry point — it isolates a skill's trace-type items (via
  // FoundationSession's itemFilter) without changing that skill's
  // audienceForSkill/subject routing. Currently only meaningful for
  // ms.math.00e (the traceNumeral content). NOT a general routing switch —
  // see FoundationSession.tsx's mode prop doc comment.
  mode?: "trace";
  // MA-1004 (R1, MA-790 resume-on-reading-foundation-mixed): the same
  // validated `resume` and `seed` as /play/session, used only when no
  // checkpoint exists.
  resume?: number;
  seed?: string;
}

export const Route = createFileRoute("/play/foundation")({
  errorComponent: KidRouteError, // MA-107
  validateSearch: (search: Record<string, unknown>): FoundationSearch => ({
    skill:
      typeof search.skill === "string" && search.skill.length > 0
        ? search.skill
        : "ms.read.01",
    // accept practice=true / practice=1 / practice="1"
    practice:
      search.practice === true ||
      search.practice === 1 ||
      search.practice === "1" ||
      search.practice === "true"
        ? true
        : undefined,
    mode: search.mode === "trace" ? "trace" : undefined,
    resume: parseResumeParam(search.resume),
    seed: parseSeedParam(search.seed),
  }),
  beforeLoad: ({ search }) => {
    const { skill, practice } = search as FoundationSearch;
    if (practice) return; // explicit parent practice — exempt from the floor.

    // Read the ACTIVE child's persisted progress (localStorage is kept current
    // by setProgress → saveProgressForChild). No active child → no floor.
    const activeId = listChildren().activeChildId ?? null;
    const progress = activeId ? loadProgressForActiveChild(activeId) : null;
    // MA-208: this guard is keyed off the READING placement floor only —
    // skillCurriculumOrdinal("reading", "ms.math.00e") is -1 (not a reading
    // module), so the skill is never below the floor and mode="trace" deep
    // links pass through untouched. Deliberate: ms.math.00e has no
    // reading-floor concept, and this route is intentionally not extended to
    // guard math skills here.
    // MA-837: the floor is the EVIDENCE floor (the mission's and the Journey
    // tap's own rule — an age seed is not a floor), with the registry age as
    // the same fallback the mission uses.
    const registryAge = activeId
      ? (listChildren().children.find((c: { id: string }) => c.id === activeId)?.age ?? null)
      : null;
    // MA-851: a lowered demotion valve on the floor skill (the child is failing it) lets the floor
    // skill's own prerequisites through, the path down the ranker already picks.
    const redirectTo = foundationGuardRedirect(skill, progress, registryAge, floorValveLoweredForChild(activeId, progress, registryAge));
    if (!redirectTo) return;
    const { floor } = redirectTo;

    // QA guard (mirrors useTodaysMission.ts's below-floor dev warn): a served
    // foundation skill below the placement floor is the class of bug that let
    // "6yo gets trace-b" ship unseen.
    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.warn(
        `[play/foundation guard] skill ${skill} is below the reading placement ` +
          `floor ${floor.skillId} (${floor.legacyId}, ord ${floor.ordinal}); ` +
          `redirecting to the placed-floor skill.`,
      );
    }

    // Serve AT the placed floor, routed to the floor skill's proper surface
    // (mirrors PathHome.navigateToSubject / SkillTree.practiceTargetForSkill):
    // a foundation-delivered floor opens FoundationSession; any other reading
    // skill opens ReadingSession. NOT READING_FALLBACK_SKILL, which can itself
    // be below the placement.
    throw redirect({ to: redirectTo.to, search: { skill: redirectTo.skillId } });
  },
  component: RouteComponent,
});

function RouteComponent() {
  const { skill, mode, resume, seed } = Route.useSearch();
  return <FoundationSession skillId={skill} mode={mode} resumeAt={resume} seed={seed} />;
}
